import React, { useCallback, useRef } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { filterSearchClassName } from "./TableStyles";

export interface SearchInputProps {
  label: string;
  placeholder: string;
  /** Fires on blur and Enter — kept for backward compat. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Fires on blur, Enter, AND after 300ms debounce on keystroke. Preferred over onBlur. */
  onSearch?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  startDecorator?: React.ReactNode;
  slotProps?: { input?: Record<string, unknown> };
  className?: string;
}

export const SearchInput = ({
  label,
  placeholder,
  onBlur,
  onSearch,
  onKeyDown,
  startDecorator = <Search className="size-4" />,
  slotProps,
  className,
}: SearchInputProps) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onSearch) return;
      clearTimeout(debounceRef.current);
      const value = e.target.value;
      debounceRef.current = setTimeout(() => onSearch(value), 300);
    },
    [onSearch],
  );

  return (
    <div className={cn(filterSearchClassName, className)}>
      <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
      <div className="relative">
        {startDecorator && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            {startDecorator}
          </div>
        )}
        <Input
          className={startDecorator ? "pl-8" : undefined}
          placeholder={placeholder}
          onChange={onSearch ? handleChange : undefined}
          onBlur={(e) => {
            clearTimeout(debounceRef.current);
            if (onSearch) onSearch(e.target.value);
            else onBlur?.(e);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              clearTimeout(debounceRef.current);
              const target = event.target as HTMLInputElement;
              if (onSearch) onSearch(target.value);
              else target.blur();
            }
            onKeyDown?.(event);
          }}
          {...slotProps?.input}
        />
      </div>
    </div>
  );
};
